import React, { useState } from "react";
import { Card, SectionLabel, Button } from "./ui";
import { useApp } from "../context/AppContext";
import { useBilling } from "../lib/useBilling";
import { PLANS, priceLabel, ANNUAL_DISCOUNT_PCT } from "../lib/plans";
import { cn } from "../lib/utils";
import { formatBytes } from "./drive/driveUtils";
import { Crown, Check, ArrowRight, Zap, Boxes, HardDrive, Eye } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);
const PURCHASABLE = ["pro", "business", "portfolio"];
const GB = 1024 ** 3;

function StorageBar({ usedBytes, gbLimit, lang }) {
  const unlimited = gbLimit == null;
  const limitBytes = unlimited ? 0 : gbLimit * GB;
  const pct = unlimited ? 8 : Math.min(100, Math.round((usedBytes / Math.max(1, limitBytes)) * 100));
  const near = !unlimited && pct >= 90;
  return (
    <div data-testid="usage-storage">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-text-secondary flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5" />{L(lang, "Stockage", "Storage")}</span>
        <span className={cn("tabular-nums font-medium", near ? "text-red-400" : "text-text-primary")}>
          {formatBytes(usedBytes)} / {unlimited ? L(lang, "illimité", "unlimited") : `${gbLimit} GB`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", near ? "bg-red-500" : "bg-primary")}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function UsageBar({ label, used, limit, lang }) {
  const unlimited = limit == null;
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const near = !unlimited && used >= limit;
  return (
    <div data-testid={`usage-${label}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-text-secondary">{label}</span>
        <span className={cn("tabular-nums font-medium", near ? "text-red-400" : "text-text-primary")}>
          {used} / {unlimited ? L(lang, "illimité", "unlimited") : limit}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", near ? "bg-red-500" : "bg-primary")}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function BillingCard() {
  const { lang } = useApp();
  const { billing, startCheckout } = useBilling();
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");

  if (!billing) return null;
  const { platform_admin: isAdmin, plan, limits = {}, usage = {}, cycle: activeCycle, current_period_end,
    is_trial: isTrial, is_free: isFree, trial_days_left: trialDaysLeft } = billing;
  const planMeta = PLANS.find((p) => p.id === plan);
  const planName = isAdmin ? L(lang, "Admin plateforme", "Platform admin") : (planMeta?.name[lang] || plan);

  const onUpgrade = async (pid) => {
    setBusy(pid);
    try { await startCheckout(pid, cycle); } catch { setBusy(""); }
  };

  const periodLabel = current_period_end
    ? new Date(current_period_end).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { year: "numeric", month: "short", day: "numeric" })
    : null;

  const upgradeOptions = PLANS.filter((p) => PURCHASABLE.includes(p.id) && (isTrial || isFree || p.id !== plan));

  return (
    <Card className="mb-6" data-testid="billing-card">
      <SectionLabel>{L(lang, "Abonnement & utilisation", "Subscription & usage")}</SectionLabel>

      <div className="flex items-center justify-between mt-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
            {isAdmin ? <Crown className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
          </div>
          <div>
            <div className="font-heading font-bold text-lg text-text-primary flex items-center gap-2" data-testid="billing-current-plan">
              {planName}
              {!isAdmin && isTrial && (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">{L(lang, "Essai", "Trial")}</span>
              )}
              {!isAdmin && isFree && (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400">{L(lang, "Gratuit", "Free")}</span>
              )}
            </div>
            <div className="text-xs text-text-secondary">
              {isAdmin
                ? L(lang, "Accès illimité, aucune facturation", "Unlimited access, no billing")
                : isTrial
                  ? L(lang, `Essai gratuit — ${trialDaysLeft ?? 0} jour(s) restant(s)`, `Free trial — ${trialDaysLeft ?? 0} day(s) left`)
                  : isFree
                    ? L(lang, "Plan Gratuit. Améliorez pour débloquer plus d'entités, le Conseil IA et plus.", "Free plan. Upgrade to unlock more entities, AI Advisory and more.")
                    : `${activeCycle === "annual" ? L(lang, "Annuel", "Annual") : L(lang, "Mensuel", "Monthly")}${periodLabel ? ` · ${L(lang, "renouvellement", "renews")} ${periodLabel}` : ""}`}
            </div>
          </div>
        </div>
      </div>

      {!isAdmin && (isTrial || isFree) && (
        <div className={cn("rounded-xl border p-3 mb-4 flex items-start gap-2.5 text-sm",
          isFree ? "border-blue-500/30 bg-blue-500/10 text-blue-300" : "border-amber-500/30 bg-amber-500/10 text-amber-200")}
          data-testid={isFree ? "billing-free-banner" : "billing-trial-banner"}>
          <Zap className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {isFree
              ? L(lang, "Vous êtes sur le plan Gratuit. Vos données sont intactes et accessibles — passez à un plan payant pour créer plus d'entités, débloquer le Conseil IA et augmenter le stockage.",
                       "You're on the Free plan. Your data is intact and accessible — upgrade to create more entities, unlock AI Advisory and get more storage.")
              : L(lang, `Il vous reste ${trialDaysLeft ?? 0} jour(s) d'essai gratuit du plan Business. Souscrivez pour conserver l'accès complet.`,
                       `You have ${trialDaysLeft ?? 0} day(s) left in your Business free trial. Subscribe to keep full access.`)}
          </span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-3">
        <UsageBar label={L(lang, "Entités", "Entities")} used={usage.entities ?? 0} limit={limits.entities} lang={lang} />
        <UsageBar label={L(lang, "Crédits IA / mois", "AI credits / month")} used={usage.ai_credits_used ?? 0} limit={limits.ai_credits} lang={lang} />
        <StorageBar usedBytes={usage.storage_used_bytes ?? 0} gbLimit={limits.storage_gb} lang={lang} />
        <UsageBar label={L(lang, "Collaborateurs", "Collaborators")} used={usage.collaborators ?? 0} limit={limits.collaborators} lang={lang} />
      </div>
      <div className="flex items-center gap-2 text-xs text-text-secondary mb-5" data-testid="usage-viewers">
        <Eye className="w-3.5 h-3.5" />
        <span>{L(lang, "Lecteurs", "Viewers")}</span>
        <span className="font-medium text-text-primary tabular-nums">{usage.viewers ?? 0}</span>
        <span>· {L(lang, "illimités et gratuits", "unlimited & free")}</span>
        {(usage.pending_collaborators ?? 0) > 0 && (
          <span className="ml-auto text-text-secondary">{usage.pending_collaborators} {L(lang, "invitation(s) en attente", "pending invite(s)")}</span>
        )}
      </div>

      {!isAdmin && upgradeOptions.length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-text-primary">{L(lang, "Changer de plan", "Change plan")}</span>
            <div className="inline-flex items-center gap-1 rounded-full bg-surface-hover p-0.5" data-testid="billing-cycle-toggle">
              {[["monthly", L(lang, "Mensuel", "Monthly")], ["annual", L(lang, "Annuel", "Annual")], ["lifetime", L(lang, "À vie", "Lifetime")]].map(([id, lab]) => (
                <button key={id} onClick={() => setCycle(id)} data-testid={`billing-cycle-${id}`}
                  className={cn("px-3 py-1 rounded-full text-xs font-semibold transition-colors",
                    cycle === id ? "bg-primary text-white" : "text-text-secondary hover:text-text-primary")}>
                  {lab}{id === "annual" && <span className="ml-1 text-[9px] text-emerald-400">-{ANNUAL_DISCOUNT_PCT}%</span>}
                  {id === "lifetime" && <span className="ml-1 text-[8px] font-bold uppercase text-amber-400">{L(lang, "Limitée", "LTD")}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {upgradeOptions.map((p) => (
              <div key={p.id} className={cn("rounded-xl border p-3 flex flex-col", p.popular ? "border-primary" : "border-border")} data-testid={`billing-plan-${p.id}`}>
                {p.popular && <span className="self-start text-[9px] font-bold uppercase tracking-wide text-primary mb-1">{L(lang, "Le + choisi", "Most popular")}</span>}
                <div className="font-heading font-semibold text-text-primary flex items-center gap-1.5">
                  {p.id === "portfolio" ? <Boxes className="w-3.5 h-3.5" /> : null}{p.name[lang]}
                </div>
                <div className="mb-2">
                  <span className="font-heading font-black text-xl text-text-primary">{priceLabel(p, lang, cycle)}</span>
                  <span className="text-text-secondary text-xs">{cycle === "lifetime" ? ` ${L(lang, "à vie", "lifetime")}` : `/${L(lang, "mois", "mo")}`}</span>
                </div>
                <Button onClick={() => onUpgrade(p.id)} disabled={busy === p.id} data-testid={`billing-upgrade-${p.id}`}
                  className="mt-auto w-full justify-center text-sm">
                  {busy === p.id ? L(lang, "Redirection…", "Redirecting…") : <>{L(lang, "Choisir", "Choose")} <ArrowRight className="w-3.5 h-3.5" /></>}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-text-secondary mt-3 flex items-start gap-1.5">
            <Check className="w-3.5 h-3.5 mt-px shrink-0 text-emerald-400" />
            {L(lang, "Collaborateurs inclus selon le plan — passez au plan supérieur pour en ajouter, sans surcoût par siège. Lecteurs gratuits. Facturation Stripe sécurisée.",
                     "Collaborators included per plan — upgrade to a higher plan to add more, no per-seat fees. Free viewers. Secure Stripe billing.")}
          </p>
        </div>
      )}
    </Card>
  );
}
